import { McButton, McLoadingIndicator, McTable } from '@maersk-global/mds-react-wrapper';
import React, { Fragment, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { gateHoursService } from '../../../common/service/gate-hours/gate-hours-service';
import {
  GateHourInfo,
  GateHoursDays,
  GateHours as GateHoursType,
  GateHoursDailyOverride,
} from '../../../common/service/gate-hours/types';
import RemarksRenderer from '../remarks-renderer';
import { columns, days } from './consts';
import GateHoursDrawer from './gate-hours-drawer';
import { parseData, parseHours } from './helpers';
import OverwriteWeekDrawer from './overwrite-week-drawer';

type GateHoursProps = {
  selectedTerminal: string;
};

export type MappedGateHours = {
  id: number;
  day: string;
  dayValue: keyof GateHoursDays;
  remarks: string;
  hours: string;
  isOpen: boolean;
  fullDayOpen?: boolean;
};

const GateHours: FunctionComponent<GateHoursProps> = ({ selectedTerminal }) => {
  const [data, setData] = useState([] as GateHourInfo[]);
  const [rawData, setRawData] = useState({} as GateHoursType);
  const [entryToEdit, setEntryToEdit] = useState<MappedGateHours | undefined>(undefined);
  const [entryToEditDateLabel, setEntryToEditDateLabel] = useState<string | undefined>(undefined);
  const [entryToEditDate, setEntryToEditDate] = useState<string | undefined>(undefined);
  const [dailyOverrides, setDailyOverrides] = useState<GateHoursDailyOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [openOverwriteWeekDrawer, setOpenOverwriteWeekDrawer] = useState(false);

  const service = useMemo(() => gateHoursService(), []);

  const fetch = async () => {
    setLoading(true);
    try {
      const [resp, overrideResp] = await Promise.all([
        service.fetchGateHours(selectedTerminal),
        service.fetchDailyOverride(selectedTerminal),
      ]);

      setData(parseData(resp));
      setDailyOverrides(overrideResp || []);

      if (resp) {
        setRawData(resp);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by design
  }, [selectedTerminal]);

  const dataMapper = useCallback(
    (data: GateHourInfo[]) =>
      data.map((h, index) => ({
        id: index,
        day: days[index],
        remarks: h.remarks || '',
        hours: h.fullDayOpen ? 'All day' : h.timeSlots === null ? 'Closed' : parseHours(h.timeSlots),
        dayValue: days[index].toUpperCase() as keyof GateHoursDays,
        isOpen: h.timeSlots !== null,
        fullDayOpen: h.fullDayOpen,
      })),
    [],
  );

  const hoursMapped = useMemo(() => dataMapper(data), [data, dataMapper]);

  const selectedDaysForEdit = useMemo(
    () =>
      entryToEdit?.hours.length
        ? [entryToEdit.dayValue]
        : hoursMapped.filter((x) => !x.hours.length).map((x) => x.dayValue),
    [hoursMapped, entryToEdit],
  );

  return (
    <div className="Events">
      {loading ? (
        <McLoadingIndicator label="Fetching data..." />
      ) : (
        <div className="Events__table-wrapper">
          <div className="Events__tilte-button">
            <h2 className="Events__title">Gate hours </h2>
            <McButton
              variant="filled"
              appearance="primary"
              icon="pencil"
              fit="small"
              label="Overwrite a week"
              click={() => setOpenOverwriteWeekDrawer(true)}
            ></McButton>
          </div>
          <McTable columns={columns} datakey="id" data={hoursMapped}>
            {hoursMapped.map((row) => (
              <Fragment key={`${row.id}_actions`}>
                <div slot={`${row.id}_actions`}>
                  <McButton
                    fit="small"
                    variant="plain"
                    appearance="primary"
                    icon="pencil"
                    click={() => {
                      setEntryToEditDate(undefined);
                      setEntryToEditDateLabel(undefined);
                      setEntryToEdit(row);
                    }}
                  >
                    Edit
                  </McButton>
                </div>
                <div slot={`${row.id}_remarks`}>{<RemarksRenderer remarks={row.remarks} />}</div>
              </Fragment>
            ))}
          </McTable>
        </div>
      )}

      {entryToEdit && (
        <GateHoursDrawer
          open={true}
          selectedTerminal={selectedTerminal}
          defaultValues={entryToEdit}
          data={rawData}
          dailyOverrides={dailyOverrides}
          openedDateLabel={entryToEditDateLabel}
          openedDate={entryToEditDate}
          // @ts-ignore
          selectedDaysForEdit={selectedDaysForEdit}
          onSuccess={fetch}
          onClose={() => {
            setEntryToEditDate(undefined);
            setEntryToEdit(undefined);
            setEntryToEditDateLabel(undefined);
          }}
        />
      )}

      {openOverwriteWeekDrawer && (
        <OverwriteWeekDrawer
          open={true}
          setOpen={setOpenOverwriteWeekDrawer}
          data={hoursMapped}
          dailyOverrides={dailyOverrides}
          onEditRow={(row) => {
            setOpenOverwriteWeekDrawer(false);
            setEntryToEdit(row);
            setEntryToEditDate(row.overrideDate);
            setEntryToEditDateLabel(`${row.day}, ${row.dateLabel}`);
          }}
          onClose={() => {
            setOpenOverwriteWeekDrawer(false);
          }}
          onSave={() => {
            setOpenOverwriteWeekDrawer(false);
          }}
        />
      )}
    </div>
  );
};

export default GateHours;
